import { option, readonlyArray, string } from 'fp-ts';
import { flow } from 'fp-ts/function';
import { normalizeResponseData } from '@mews-commander/legacy-data-structures';
import { GUID_PATTERN } from '@mews/framework';

export type SearchParam = string | string[];

export const getUrlParameter = (urlParameter: string): SearchParam | undefined => {
    const url = new URL(global.location.href);
    const searchParams = getUrlParameters(url, true);
    return searchParams[urlParameter];
};

export const getUrlParameters = (url: URL, normalize = false): Record<string, SearchParam> => {
    const params: Record<string, SearchParam> = {};
    Array.from(url.searchParams.entries()).forEach(([key, val]) => {
        if (params[key]) {
            if (typeof params[key] === 'string') {
                params[key] = [params[key]];
            }
            if (Array.isArray(params[key])) {
                params[key].push(val);
            }
        } else {
            params[key] = val;
        }
    });
    return normalize ? normalizeResponseData(params) : params;
};

/** constructing an URL object from a standalone path (just /commander/xxx) throws a TypeError */
const dummyBaseUrl = 'http://example.com';
export const createUrlFromPath = (path: string) => new URL(path, dummyBaseUrl);

export const getPathFromUrl = (url: URL, includeHash = false) =>
    `${url.pathname}${url.search}${includeHash ? url.hash : ''}`;

export const getCurrentUrl = () => {
    const url = new URL(global.location.href);
    return getPathFromUrl(url, true);
};

export const setUrlParams = (params: Record<string, string>) => {
    const newUrl = new URL(global.location.href);
    Object.keys(params).forEach((key) => {
        newUrl.searchParams.set(key, params[key]);
    });
    return getPathFromUrl(newUrl, true);
};

export const removeUrlParam = (param: string) => {
    const newUrl = new URL(global.location.href);
    newUrl.searchParams.delete(param);
    return getPathFromUrl(newUrl, true);
};

export const openInBrowserTab = (url: string) => window.open(url, '_blank', 'noopener, noreferrer');

const stripParamsFromUrl = flow(
    string.split('?'),
    readonlyArray.head,
    option.getOrElse(() => '')
);
const multipleSlashPattern = new RegExp('/+$', 'g');
const guidPattern = new RegExp(GUID_PATTERN, 'g');

export const anonymizeUrl = flow(
    string.replace(guidPattern, ''),
    stripParamsFromUrl,
    string.replace(multipleSlashPattern, ''),
    string.toLowerCase
);
